/**
 * Cryptographic Service
 *
 * Provides cryptographic operations for contract signing:
 * - SHA-256 hashing of contract content
 * - Ed25519 digital signatures (RFC8032 compliant)
 * - Signature verification
 * - Key generation
 *
 * Uses:
 * - @noble/ed25519: RFC8032 compliant Ed25519 signatures
 * - Node.js crypto: SHA-256 hashing
 */
import { createHash } from 'crypto';
import * as ed from '@noble/ed25519';

import { getLogger } from '@kit/shared/logger';

/**
 * Result type for signature operations
 */
export interface SignatureResult {
  success: boolean;
  data?: string;
  error?: string;
}

/**
 * Signature data with metadata
 */
export interface SignatureWithMetadata {
  signature: string; // Hex-encoded Ed25519 signature
  contractHash: string; // SHA-256 hash of contract
  publicKey: string; // Hex-encoded public key
  timestamp: string; // ISO 8601 timestamp
  algorithm: 'Ed25519'; // Signature algorithm
  hashAlgorithm: 'SHA-256'; // Hash algorithm
}

/**
 * Cryptographic Service for contract signing
 *
 * Handles all cryptographic operations needed for legally-binding digital signatures.
 * All signatures are RFC8032 compliant and suitable for legal/financial purposes.
 */
export class CryptoService {
  /**
   * Hash contract content using SHA-256
   *
   * @param content - Contract content (text or JSON string)
   * @returns SHA-256 hash in hex format
   */
  static hashContract(content: string): string {
    return createHash('sha256').update(content).digest('hex');
  }

  /**
   * Generate an Ed25519 keypair for signing
   *
   * Should be called server-side and stored securely.
   * Private key must be kept secret and never exposed to client.
   *
   * @returns Keypair with private key (hex) and public key (hex)
   */
  static async generateKeyPair(): Promise<{
    privateKey: string;
    publicKey: string;
  }> {
    try {
      // Generate random private key
      const privateKeyBytes = ed.utils.randomSecretKey();
      const privateKey = Buffer.from(privateKeyBytes).toString('hex');

      // Derive public key from private key
      const publicKeyBytes = await ed.getPublicKeyAsync(privateKeyBytes);
      const publicKey = Buffer.from(publicKeyBytes).toString('hex');

      return { privateKey, publicKey };
    } catch (error) {
      throw new Error(
        `Failed to generate keypair: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Sign a contract hash using Ed25519
   *
   * This creates a legally-binding signature proof.
   * The signature is generated using the Flow server's private key.
   *
   * @param contractHash - SHA-256 hash of contract content
   * @param privateKey - Hex-encoded Ed25519 private key
   * @returns Signature in hex format
   */
  static async signContractHash(
    contractHash: string,
    privateKey: string,
  ): Promise<SignatureResult> {
    try {
      // Validate inputs
      if (!contractHash || contractHash.length !== 64) {
        return {
          success: false,
          error:
            'Invalid contract hash: must be 64 character SHA-256 hex string',
        };
      }

      if (!privateKey || privateKey.length !== 128) {
        return {
          success: false,
          error: 'Invalid private key: must be 128 character hex string',
        };
      }

      // Convert hex strings to bytes
      const hashBytes = Buffer.from(contractHash, 'hex');
      const privateKeyBytes = Buffer.from(privateKey, 'hex');

      // Sign the contract hash
      const signatureBytes = await ed.signAsync(hashBytes, privateKeyBytes);
      const signature = Buffer.from(signatureBytes).toString('hex');

      return {
        success: true,
        data: signature,
      };
    } catch (error) {
      return {
        success: false,
        error: `Failed to sign contract: ${error instanceof Error ? error.message : String(error)}`,
      };
    }
  }

  /**
   * Verify a contract signature
   *
   * Validates that a signature was created using the corresponding private key.
   * Used for audit trail and verification purposes.
   *
   * @param contractHash - SHA-256 hash of contract content
   * @param signature - Hex-encoded Ed25519 signature
   * @param publicKey - Hex-encoded public key
   * @returns True if signature is valid, false otherwise
   */
  static async verifySignature(
    contractHash: string,
    signature: string,
    publicKey: string,
  ): Promise<boolean> {
    try {
      const logger = await getLogger();

      // Validate inputs
      if (!contractHash || contractHash.length !== 64) {
        logger.error(
          { name: 'verify-signature', field: 'contractHash' },
          'Invalid contract hash',
        );
        return false;
      }

      if (!signature || signature.length !== 128) {
        logger.error(
          { name: 'verify-signature', field: 'signature' },
          'Invalid signature length',
        );
        return false;
      }

      if (!publicKey || publicKey.length !== 64) {
        logger.error(
          { name: 'verify-signature', field: 'publicKey' },
          'Invalid public key length',
        );
        return false;
      }

      // Convert hex strings to bytes
      const hashBytes = Buffer.from(contractHash, 'hex');
      const signatureBytes = Buffer.from(signature, 'hex');
      const publicKeyBytes = Buffer.from(publicKey, 'hex');

      // Verify the signature
      return await ed.verifyAsync(signatureBytes, hashBytes, publicKeyBytes);
    } catch (error) {
      const logger = await getLogger();
      logger.error(
        {
          name: 'verify-signature',
          error: error instanceof Error ? error.message : String(error),
        },
        'Signature verification error',
      );
      return false;
    }
  }

  /**
   * Create a complete signature with metadata
   *
   * Combines hash, signature, and metadata into a single verifiable proof.
   * This is the complete audit trail for a signed contract.
   *
   * @param contractContent - Original contract text
   * @param signature - Hex-encoded Ed25519 signature
   * @param publicKey - Hex-encoded public key
   * @returns Signature with full metadata
   */
  static createSignatureWithMetadata(
    contractContent: string,
    signature: string,
    publicKey: string,
  ): SignatureWithMetadata {
    return {
      signature,
      contractHash: this.hashContract(contractContent),
      publicKey,
      timestamp: new Date().toISOString(),
      algorithm: 'Ed25519',
      hashAlgorithm: 'SHA-256',
    };
  }

  /**
   * Verify complete signature with metadata
   *
   * @param contractContent - Original contract text
   * @param metadata - Signature with metadata
   * @returns True if signature is valid, false otherwise
   */
  static async verifySignatureWithMetadata(
    contractContent: string,
    metadata: SignatureWithMetadata,
  ): Promise<boolean> {
    // Recalculate hash and compare
    const expectedHash = this.hashContract(contractContent);
    if (expectedHash !== metadata.contractHash) {
      const logger = await getLogger();
      logger.error(
        {
          name: 'verify-signature-metadata',
          expectedHash,
          actualHash: metadata.contractHash,
        },
        'Contract hash mismatch - content may have been modified',
      );
      return false;
    }

    // Verify signature
    return this.verifySignature(
      metadata.contractHash,
      metadata.signature,
      metadata.publicKey,
    );
  }

  /**
   * Hash a JSON object for signing
   *
   * Ensures consistent hashing across different representations.
   * Useful for hashing structured data before signing.
   *
   * @param data - Object to hash
   * @returns SHA-256 hash in hex format
   */
  static hashJSON(data: Record<string, unknown>): string {
    const jsonString = JSON.stringify(data, null, 2);
    return this.hashContract(jsonString);
  }

  /**
   * Create a signature proof combining user action and server signature
   *
   * This creates a legally-binding proof that:
   * 1. User reviewed and accepted the contract (canvas signature)
   * 2. Server witnessed and recorded the acceptance (Ed25519 signature)
   * 3. Contract content hasn't been modified (SHA-256 hash)
   * 4. All actions are timestamped and logged (audit trail)
   *
   * @param contractHash - SHA-256 hash of contract
   * @param userSignatureImageUrl - URL to user's canvas/typed signature
   * @param serverPrivateKey - Server's Ed25519 private key
   * @returns Complete signature proof with metadata
   */
  static async createCompleteSignatureProof(
    contractHash: string,
    userSignatureImageUrl: string,
    serverPrivateKey: string,
    metadata?: {
      ipAddress?: string;
      userAgent?: string;
      timestamp?: string;
    },
  ): Promise<{
    success: boolean;
    proof?: {
      contractHash: string;
      userSignatureImageUrl: string;
      serverSignature: string;
      serverPublicKey: string;
      timestamp: string;
      ipAddress?: string;
      userAgent?: string;
    };
    error?: string;
  }> {
    try {
      // Sign the contract hash with server key
      const signResult = await this.signContractHash(
        contractHash,
        serverPrivateKey,
      );
      if (!signResult.success || !signResult.data) {
        return {
          success: false,
          error: signResult.error,
        };
      }

      // Get the public key from private key
      const privateKeyBytes = Buffer.from(serverPrivateKey, 'hex');
      const publicKeyBytes = await ed.getPublicKeyAsync(privateKeyBytes);
      const serverPublicKey = Buffer.from(publicKeyBytes).toString('hex');

      return {
        success: true,
        proof: {
          contractHash,
          userSignatureImageUrl,
          serverSignature: signResult.data,
          serverPublicKey,
          timestamp: metadata?.timestamp || new Date().toISOString(),
          ipAddress: metadata?.ipAddress,
          userAgent: metadata?.userAgent,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: `Failed to create signature proof: ${error instanceof Error ? error.message : String(error)}`,
      };
    }
  }
}

/**
 * Export crypto utilities for use in other services
 */
export const cryptoUtils = {
  hashContract: CryptoService.hashContract,
  hashJSON: CryptoService.hashJSON,
  generateKeyPair: CryptoService.generateKeyPair,
  signContractHash: CryptoService.signContractHash,
  verifySignature: CryptoService.verifySignature,
  createSignatureWithMetadata: CryptoService.createSignatureWithMetadata,
  verifySignatureWithMetadata: CryptoService.verifySignatureWithMetadata,
};
